import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import {
  MODULE_DESTINATION_IDS,
  MODULE_DESTINATIONS,
  type ModuleDestinationId,
  resolveDestinationState,
  useDestinationAccess,
} from "@/navigation";

import NavBarIcon from "../components/NavBarIcon";
import { NAVBAR_LINK_PRESENTATION } from "../constants/navbar-links";
import type { NavBarLink } from "../types/navbar";

interface UseNavBarLinksArgs {
  unreadCommunicationsCount: number;
  labOrdersCount: number;
}

/**
 * Builds the side nav links the current user can see, in registry order.
 *
 * @param args.unreadCommunicationsCount - Count shown on the Messages badge.
 * @param args.labOrdersCount - Count shown on the Tests badge.
 * @returns The visible links, each with its path under the active practice.
 */
export function useNavBarLinks({
  unreadCommunicationsCount,
  labOrdersCount,
}: UseNavBarLinksArgs): NavBarLink[] {
  const navigate = useNavigate();
  const access = useDestinationAccess();

  return useMemo(() => {
    const badgeCounts: Partial<Record<ModuleDestinationId, number>> = {
      [MODULE_DESTINATION_IDS.messages]: unreadCommunicationsCount,
      [MODULE_DESTINATION_IDS.tests]: labOrdersCount,
    };

    return MODULE_DESTINATIONS.reduce<NavBarLink[]>((links, destination) => {
      if (resolveDestinationState(destination.gates, access) !== "visible") return links;

      const path = destination.buildPath(access);

      const { type, activePaths, excludePaths, badgeAriaLabel } =
        NAVBAR_LINK_PRESENTATION[destination.id];
      const icon = (
        <NavBarIcon
          Icon={destination.Icon}
          ariaLabel={destination.label}
          badge={
            badgeAriaLabel
              ? { count: badgeCounts[destination.id] ?? 0, ariaLabel: badgeAriaLabel }
              : undefined
          }
        />
      );
      const common = { label: destination.label, icon, activePaths, excludePaths };

      if (type === "anchor") {
        links.push({ ...common, type, to: path });
        return links;
      }

      links.push({
        ...common,
        type,
        onClick: () => navigate(path),
      });
      return links;
    }, []);
  }, [access, unreadCommunicationsCount, labOrdersCount, navigate]);
}
